import { DependencyList, useEffect, useState } from 'react';

import { createSubject, Observer, Subject, Subscription } from './subject';

/**
 * Simple BehaviorSubject inspired by https://rxjs.dev/guide/subject#behaviorsubject.
 * Unlike Subject, BehaviorSubject holds a current value and emits it immediately
 * to new subscribers.
 *
 *  Parent:
 *      const [count] = useState(() => createBehaviorSubject(0));
 *      <button onClick={() => count.next(count.getValue() + 1)}>Increment</button>
 *      <Child count={count} />
 *  Child:
 *      const value = useBehaviorSubjectValue(count, []);
 */
export type BehaviorSubject<T> = Subject<T> & {
  getValue: () => T;
};

export const createBehaviorSubject = <T>(
  initialValue: T,
): BehaviorSubject<T> => {
  const subject = createSubject<T>();
  let currentValue = initialValue;

  return {
    get observers() {
      return subject.observers;
    },
    getValue: () => currentValue,
    next: (value: T) => {
      currentValue = value;
      subject.next(value);
    },
    subscribe: (observer: Observer<T>): Subscription => {
      const subscription = subject.subscribe(observer);
      observer.next(currentValue);
      return subscription;
    },
    unsubscribe: subject.unsubscribe,
  };
};

/**
 * Returns the current value of a BehaviorSubject and re-renders when it changes.
 */
export const useBehaviorSubjectValue = <T>(
  subject: BehaviorSubject<T>,
  deps: DependencyList,
): T => {
  const [value, setValue] = useState(() => subject.getValue());

  useEffect(() => {
    const subscription = subject.subscribe({ next: setValue });
    return () => {
      subscription.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subject, ...(deps || [])]);

  return value;
};
